import includes from 'lodash/includes';
import isArray from 'lodash/isArray';
import isUndefined from 'lodash/isUndefined';
import times from 'lodash/times';
import size from 'lodash/size';
import Arg from 'arg';
import {maps as gmaps} from 'google';
import StorageUtils from 'storage-utils';
import ObjectUtils from '@spothero/utils/object';
import SHGlobals from 'sh-globals';

let savedZoom;
let previousInnerCoords;

const SearchUtils = {
    prepareStoreData(initData) {
        const filterState = StorageUtils.get('search-filters', 'session');
        const queryParams = Arg.query();
        const {
            referrer,
            sha_affiliate,
            ...queryString
        } = queryParams;

        // ensure monthly query string is a proper js Boolean (ie true not True)
        if (queryString.monthly) {
            queryString.monthly = Boolean(queryString.monthly);
        }

        // if queryString.monthly is false clear in out in favor of initData search params
        if (!queryString.monthly) {
            delete queryString.monthly;
        }

        if (!isUndefined(referrer)) {
            queryString.mixpanelReferrer = referrer;
        }

        if (!isUndefined(sha_affiliate)) {
            queryString.mixpanelReferrerAffiliate = sha_affiliate;
        }

        const data = {
            search: {
                isPending: false,
                data: {
                    stripeAPIKey: initData.stripeAPIKey,
                    breadcrumbs: initData.breadcrumbs,
                    isDefaultTimes: (queryString.starts) ? false : true,
                    ...initData.search,
                    params: {
                        ...initData.search.params,
                        ...queryString
                    }
                },
                error: null
            }
        };

        if (initData.spots) {
            data.spots = {
                isPending: false,
                data: initData.spots,
                error: null
            };
        }

        if (window.spotHeroUser) {
            data.user = {
                isPending: false,
                data: {
                    ...window.spotHeroUser
                },
                error: null
            };
        }

        if (initData.search.isAirport) {
            data.search.data.sortBy = 'recommended';
        }

        if (filterState) {
            data.filters = filterState;
        }

        StorageUtils.set('previous-search', {
            url: window.location.href,
            title: data.search.data.destinationTitle
        }, 'session');

        return data;
    },

    geocode(address, cb) {
        const geocoder = new gmaps.Geocoder();

        geocoder.geocode({
            address
        }, (res, status) => {
            let results = res;

            if (status !== gmaps.GeocoderStatus.OK) {
                results = [];
            }

            if (isArray(results) && results.length === 1) {
                results = SearchUtils.getAddressData(results[0]);
            }

            cb(results);
        });
    },

    getAddressData(geocodeData) {
        const {
            address_components,
            formatted_address,
            geometry,
            id,
            place_id,
            reference
        } = geocodeData;
        const data = {
            search_string: formatted_address,
            latitude: (geometry) ? geometry.location.lat() : null,
            longitude: (geometry) ? geometry.location.lng() : null,
            places_id: id,
            places_place_id: place_id,
            places_reference: reference
        };

        if (address_components) {
            address_components.forEach(item => {
                if (includes(item.types, 'street_number')) {
                    if (!('street' in data)) {
                        data.street = item.long_name;
                    } else {
                        data.street = `${item.long_name} ${data.street}`;
                    }
                } else if (includes(item.types, 'route')) {
                    if (!('street' in data)) {
                        data.street = item.long_name;
                    } else {
                        data.street = `${data.street} ${item.long_name}`;
                    }
                } else if (includes(item.types, 'locality')) {
                    data.city = item.long_name;
                } else if (includes(item.types, 'administrative_area_level_1')) {
                    data.state = item.short_name;
                } else if (includes(item.types, 'postal_code')) {
                    data.zipCode = item.long_name;
                }
            });
        }

        return data;
    },

    createTiles(bounds, searchState) {
        const {
            zoom
        } = searchState;
        const boundsNE = bounds.getNorthEast();
        const boundsSW = bounds.getSouthWest();
        const boundsNW = new gmaps.LatLng(boundsNE.lat(), boundsSW.lng());
        const boundsSE = new gmaps.LatLng(boundsSW.lat(), boundsNE.lng());
        const mapWidth = boundsNE.lng() - boundsNW.lng();
        const mapHeight = boundsNE.lat() - boundsSE.lat();
        const preparedData = {};
        const tiles = [];
        let tileCount;
        let tilesHigh;
        let tileWidth;
        let tileHeight;
        let xIncrement = 0;
        let yIncrement = 0;
        let i = 1;

        // if zooming out include previous bounds as inner coords
        if (savedZoom > zoom) { // zooming out
            preparedData.innerCoords = previousInnerCoords;
        }

        // store current coords to remove from next zoom out search
        // round lat/lng to 5 decimals (provides accuracy to roughly 1.11 m)
        // re: http://stackoverflow.com/questions/7167604/how-accurately-should-i-store-latitude-and-longitude
        previousInnerCoords = `(${boundsNW.lat().toFixed(5)},${boundsNW.lng().toFixed(5)}),(${boundsNE.lat().toFixed(5)},${boundsNE.lng().toFixed(5)}),(${boundsSE.lat().toFixed(5)},${boundsSE.lng().toFixed(5)}),(${boundsSW.lat().toFixed(5)},${boundsSW.lng().toFixed(5)}),(${boundsNW.lat().toFixed(5)},${boundsNW.lng().toFixed(5)})`;

        // adjust number of tiles and tile sizes based on current zoom level
        if (zoom < 16) {
            tileCount = 6;
            tilesHigh = 2;
            tileWidth = mapWidth / 3;
            tileHeight = mapHeight / 2;
        } else if (zoom < 18) {
            tileCount = 4;
            tilesHigh = 2;
            tileWidth = mapWidth / 2;
            tileHeight = mapHeight / 2;
        } else if (zoom < 20) {
            tileCount = 2;
            tilesHigh = 1;
            tileWidth = mapWidth / 2;
            tileHeight = mapHeight;
        } else {
            tileCount = 1;
            tilesHigh = 1;
            tileWidth = mapWidth;
            tileHeight = mapHeight;
        }

        // create tiles
        for (i; i <= tileCount; i++) {
            let tileOffsetY;
            let tileOffsetX;
            let northLat;
            let westLng;
            let southLat;
            let eastLng;

            // tiles with multiples of tilesHigh have y offset 0, x offset is tileWidth * (index * tilesHigh)
            if (i % tilesHigh === 0) {
                tileOffsetY = tileHeight * yIncrement;
                tileOffsetX = tileWidth * xIncrement;
                xIncrement++;
                yIncrement = 0;
                // odd number tiles have y offset of one tileHeight, x offset increments each odd tile
            } else {
                tileOffsetY = tileHeight * yIncrement;
                tileOffsetX = tileWidth * xIncrement;
                yIncrement++;
            }

            northLat = bounds.getNorthEast().lat() - tileOffsetY;
            westLng = bounds.getSouthWest().lng() + tileOffsetX;
            southLat = northLat - tileHeight;
            eastLng = westLng + tileWidth;

            // push tile LatLngBounds object to tiles array
            tiles.push(new gmaps.LatLngBounds(
                new gmaps.LatLng(southLat, westLng),
                new gmaps.LatLng(northLat, eastLng)
            ));
        }

        savedZoom = zoom;
        preparedData.numRequests = tileCount;
        preparedData.tiles = tiles;

        return preparedData;
    },

    getTileData(tile, map) {
        const currentTilePathObj = {
            ne: tile.getNorthEast(),
            sw: tile.getSouthWest()
        };
        const currentTileCenter = tile.getCenter();

        currentTilePathObj.nw = new gmaps.LatLng(currentTilePathObj.ne.lat(), currentTilePathObj.sw.lng());
        currentTilePathObj.se = new gmaps.LatLng(currentTilePathObj.sw.lat(), currentTilePathObj.ne.lng());

        // display tiles on map with random color
        if (window.shTestInfo.isRunningTest) {
            const currentTilePolygon = new gmaps.Polygon({
                paths: [currentTilePathObj.ne, currentTilePathObj.se, currentTilePathObj.sw, currentTilePathObj.nw],
                fillColor: `#${Math.floor(Math.random() * 16777215).toString(16)}`
            });

            currentTilePolygon.setMap(map);
        }

        // round lat/lng to 5 decimals (provides accuracy to roughly 1.11 m)
        // re: http://stackoverflow.com/questions/7167604/how-accurately-should-i-store-latitude-and-longitude
        return {
            outer_coords: `(${currentTilePathObj.nw.lat().toFixed(5)},${currentTilePathObj.nw.lng().toFixed(5)}),(${currentTilePathObj.ne.lat().toFixed(5)},${currentTilePathObj.ne.lng().toFixed(5)}),(${currentTilePathObj.se.lat().toFixed(5)},${currentTilePathObj.se.lng().toFixed(5)}),(${currentTilePathObj.sw.lat().toFixed(5)},${currentTilePathObj.sw.lng().toFixed(5)}),(${currentTilePathObj.nw.lat().toFixed(5)},${currentTilePathObj.nw.lng().toFixed(5)})`,
            latitude: currentTileCenter.lat(),
            longitude: currentTileCenter.lng()
        };
    },

    getShareURL({
        transientStarts,
        transientEnds,
        monthlyStarts,
        isMonthly,
        searchString,
        latitude,
        longitude
    }) {
        const isDestinationSearch = !includes(window.location.href, 'search');
        const params = {
            starts: (isMonthly) ? monthlyStarts.split('T')[0] : transientStarts
        };

        if (!isMonthly) {
            params.ends = transientEnds;
        } else {
            params.monthly = true;
        }

        if (!isDestinationSearch) {
            params.latitude = latitude;
            params.longitude = longitude;

            if (searchString) {
                params.search_string = searchString;
            }
        }

        return `${window.location.href.split('?')[0]}?${ObjectUtils.querify(params)}`;
    },

    prepareRequest(requestType, bounds, searchState) {
        let newSearchState = {
            ...searchState,
            requestIncrements: 5,
            params: {
                ...searchState.params,
                include: SHGlobals.FACILITY_INCLUDES,
                sort: 'distance',
                source: (SHGlobals.isMobile) ? 'web_mobile' : 'web',
                mobile_only: false
            }
        };

        if (requestType === 'tiled') {
            const {
                tiles,
                numRequests,
                innerCoords
            } = SearchUtils.createTiles(bounds, newSearchState);
            newSearchState.tiles = tiles;
            newSearchState.requestIncrements = numRequests;

            if (innerCoords) {
                newSearchState.params.inner_coords = innerCoords;
            }
        } else if (requestType === 'radial') {
            const center = bounds.getCenter();
            const maxDistance = gmaps.geometry.spherical.computeDistanceBetween(center, bounds.getNorthEast()) * 0.9;
            const distanceDelta = (maxDistance / newSearchState.requestIncrements).toFixed(2);
            const distances = [];
            let distanceIterator = 0;

            times(newSearchState.requestIncrements + 1, () => {
                distances.push(distanceDelta * distanceIterator);
                distanceIterator++;
            });

            newSearchState.params.latitude = center.lat();
            newSearchState.params.longitude = center.lng();
            newSearchState.radialDistances = distances;
        }

        return newSearchState;
    },

    updateURL({searchParams, updateDateTime = false}) {
        const cleanedUrl = window.location.href.split(/[?#]/)[0];
        const urlParams = {};
        const {
            monthly,
            latitude,
            longitude,
            search_string,
            eid,
            starts,
            ends,
            rebook_reservation_id
        } = searchParams;

        if (!includes(cleanedUrl, '-parking')) {
            urlParams.latitude = latitude;
            urlParams.longitude = longitude;

            if (search_string) {
                urlParams.search_string = search_string;
            }
        }

        if (monthly) {
            urlParams.monthly = monthly;
        }

        if (eid) {
            if (cleanedUrl.endsWith('-parking')) {
                urlParams.eid = eid;
            }
        }

        if (updateDateTime && starts && ends) {
            urlParams.starts = starts;
            urlParams.ends = ends;
        }

        if (rebook_reservation_id) {
            urlParams.rebook_reservation_id = rebook_reservation_id;
        }

        const urlParamsSize = size(urlParams);

        window.history.replaceState(
            (urlParamsSize) ? urlParams : null,
            document.title,
            (urlParamsSize) ? `?${ObjectUtils.querify(urlParams)}` : cleanedUrl
        );
    }
};

export default SearchUtils;



// WEBPACK FOOTER //
// ./spothero/static/main/js/utils/search.js